import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { adminGetMetrics } from '../../utils/api';
import AdminNav from './AdminNav';

const StatCard = ({ label, value, sub }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
    <p className="text-sm text-gray-400">{label}</p>
    <p className="text-3xl font-semibold text-white mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const AdminOverview = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminGetMetrics(currentUser.token)
      .then((data) => { if (!cancelled) setMetrics(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load metrics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser]);

  const byModality = metrics?.analyses?.by_modality || [];
  const byReview = metrics?.analyses?.by_review_status || [];
  const pendingCount = byReview.find((r) => r._id === 'pending_review')?.count || 0;
  const modelStatus = metrics?.models?.status || {};
  const modelSource = metrics?.models?.source || {};
  const labelDatasets = metrics?.zemedic_label?.datasets || [];
  const trainingRuns = metrics?.zemedic_label?.training_runs || [];

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          <h1 className="heading-secondary mb-2">Superadmin Dashboard</h1>
          <p className="text-gray-400 mb-6">Cross-system view across ZemedicMVP and ZemedicLabel.</p>
          <AdminNav />

          {loading && <p className="text-gray-400">Loading metrics…</p>}
          {error && <p className="text-red-400">{error}</p>}

          {metrics && (
            <div className="space-y-10">
              <section>
                <h2 className="heading-subsection mb-4">Analyses</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total analyses" value={metrics.analyses.total} />
                  <StatCard label="Pending review" value={pendingCount} />
                  {byModality.map((m) => (
                    <StatCard key={m._id} label={m._id} value={m.count} />
                  ))}
                </div>
              </section>

              <section>
                <h2 className="heading-subsection mb-4">Model status</h2>
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/60 text-gray-400">
                      <tr>
                        <th className="text-left px-4 py-2">Modality</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2">Weights source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(modelStatus).map((k) => (
                        <tr key={k} className="border-t border-gray-800">
                          <td className="px-4 py-2 capitalize">{k}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              modelStatus[k] === 'ready' ? 'bg-green-900/40 text-green-400' : 'bg-yellow-900/40 text-yellow-400'
                            }`}>
                              {modelStatus[k]}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-400">{modelSource[k] || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="heading-subsection mb-4">ZemedicLabel — labeling progress</h2>
                {!metrics.zemedic_label?.connected ? (
                  <p className="text-gray-500 text-sm">ZemedicLabel is unreachable right now.</p>
                ) : labelDatasets.length === 0 ? (
                  <p className="text-gray-500 text-sm">No datasets yet.</p>
                ) : (
                  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800/60 text-gray-400">
                        <tr>
                          <th className="text-left px-4 py-2">Dataset</th>
                          <th className="text-left px-4 py-2">Modality</th>
                          <th className="text-left px-4 py-2">Owner</th>
                          <th className="text-left px-4 py-2">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labelDatasets.map((d) => (
                          <tr key={d.id} className="border-t border-gray-800">
                            <td className="px-4 py-2">{d.name}</td>
                            <td className="px-4 py-2 capitalize">{d.modality}</td>
                            <td className="px-4 py-2 text-gray-400">{d.owner?.name || d.owner?.email || '—'}</td>
                            <td className="px-4 py-2">{d.labelled_count}/{d.image_count} ({d.progress_pct}%)</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section>
                <h2 className="heading-subsection mb-4">Training runs</h2>
                {!metrics.zemedic_label?.connected ? (
                  <p className="text-gray-500 text-sm">ZemedicLabel is unreachable right now.</p>
                ) : trainingRuns.length === 0 ? (
                  <p className="text-gray-500 text-sm">No training runs recorded yet.</p>
                ) : (
                  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800/60 text-gray-400">
                        <tr>
                          <th className="text-left px-4 py-2">Model</th>
                          <th className="text-left px-4 py-2">Dataset</th>
                          <th className="text-left px-4 py-2">Val accuracy</th>
                          <th className="text-left px-4 py-2">Finished</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trainingRuns.map((r) => (
                          <tr key={r.id} className="border-t border-gray-800">
                            <td className="px-4 py-2 capitalize">{r.model}</td>
                            <td className="px-4 py-2">{r.dataset}</td>
                            <td className="px-4 py-2">{r.val_accuracy != null ? `${(r.val_accuracy * 100).toFixed(1)}%` : '—'}</td>
                            <td className="px-4 py-2 text-gray-400">{r.finished_at ? new Date(r.finished_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section>
                <h2 className="heading-subsection mb-4">Active-learning queue</h2>
                {!metrics.active_learning?.connected ? (
                  <p className="text-gray-500 text-sm">ZemedicLabel is unreachable right now.</p>
                ) : Object.keys(metrics.active_learning.queues || {}).length === 0 ? (
                  <p className="text-gray-500 text-sm">No low-confidence predictions queued.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(metrics.active_learning.queues).map(([modality, q]) => (
                      <StatCard key={modality} label={modality} value={q.pending} sub={`${q.labelled} labelled`} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminOverview;
